import React, { useCallback, useEffect, useState } from 'react'
import { BookCheck, Check, LoaderCircle, Plus, RefreshCw, XCircle } from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

const EMPTY = {
  code: '', title: '', insuranceTypeLabel: '', sourceLabel: '', effectiveFrom: '',
  facts: '', guardrails: '', nextActions: '',
}

function lines(value) {
  return String(value || '').split('\n').map((item) => item.trim()).filter(Boolean)
}

export default function CounselPracticeKnowledgeManager() {
  const [data, setData] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(unwrap(await api.get('/counsel-practice/admin/knowledge', { __silent: true })))
    } catch (e) {
      setError(getApiErrorMessage(e, '상담 지식을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  const create = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    setNotice('')
    try {
      await api.post('/counsel-practice/admin/knowledge', {
        code: form.code,
        title: form.title,
        insuranceTypeLabel: form.insuranceTypeLabel,
        sourceLabel: form.sourceLabel,
        effectiveFrom: form.effectiveFrom,
        effectiveTo: null,
        facts: lines(form.facts),
        guardrails: lines(form.guardrails),
        nextActions: lines(form.nextActions),
      }, { __silent: true })
      setForm(EMPTY)
      setNotice('상담 지식 초안을 만들었습니다.')
      await load()
    } catch (e) {
      setError(getApiErrorMessage(e, '상담 지식 초안을 만들지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async (item, action) => {
    setSaving(true)
    setError('')
    setNotice('')
    try {
      await api.post(`/counsel-practice/admin/knowledge/${item.id}/${action}`, null, { __silent: true })
      setNotice(action === 'approve' ? '상담 지식을 승인했습니다.' : '상담 지식 사용을 종료했습니다.')
      await load()
    } catch (e) {
      setError(getApiErrorMessage(e, '상담 지식 상태를 변경하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const items = Array.isArray(data?.items) ? data.items : []

  return (
    <section className="mt-8 border-t border-slate-200 pt-6" data-testid="counsel-knowledge-manager" aria-labelledby="counsel-knowledge-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="counsel-knowledge-title" className="flex items-center gap-2 text-lg font-semibold text-slate-950"><BookCheck className="h-5 w-5 text-teal-700" aria-hidden="true" />상담 지식 관리</h2>
          <p className="mt-1 text-sm text-slate-600">승인된 적용 기간의 지식만 가상 고객 대화에 반영됩니다.</p>
        </div>
        <button type="button" onClick={load} disabled={loading || saving} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50" aria-label="상담 지식 새로고침" title="새로고침"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /></button>
      </div>

      {error && <div className="mt-4 border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">{error}</div>}
      {notice && <div className="mt-4 border-l-2 border-emerald-500 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">{notice}</div>}

      <form onSubmit={create} className="mt-5 grid gap-4 border-y border-slate-200 py-5 lg:grid-cols-4">
        <label className="block"><span className="text-xs font-medium text-slate-600">지식 코드</span><input value={form.code} onChange={(e) => update('code', e.target.value)} maxLength={50} required className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
        <label className="block lg:col-span-2"><span className="text-xs font-medium text-slate-600">제목</span><input value={form.title} onChange={(e) => update('title', e.target.value)} maxLength={150} required className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
        <label className="block"><span className="text-xs font-medium text-slate-600">적용일</span><input type="date" value={form.effectiveFrom} onChange={(e) => update('effectiveFrom', e.target.value)} required className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
        <label className="block"><span className="text-xs font-medium text-slate-600">보험 종류</span><input value={form.insuranceTypeLabel} onChange={(e) => update('insuranceTypeLabel', e.target.value)} maxLength={100} required className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
        <label className="block lg:col-span-3"><span className="text-xs font-medium text-slate-600">출처</span><input value={form.sourceLabel} onChange={(e) => update('sourceLabel', e.target.value)} maxLength={200} required className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
        {[
          ['facts', '확인 사실'], ['guardrails', '금지·주의'], ['nextActions', '안전한 다음 행동'],
        ].map(([key, label]) => <label key={key} className="block"><span className="text-xs font-medium text-slate-600">{label}</span><textarea value={form[key]} onChange={(e) => update(key, e.target.value)} rows={4} className="mt-1 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-5" placeholder="한 줄에 한 항목" /></label>)}
        <div className="flex items-end"><button type="submit" disabled={saving} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">{saving ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}초안 만들기</button></div>
      </form>

      <div className="mt-5 overflow-x-auto border-y border-slate-200">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-3 font-medium">지식</th><th className="px-4 py-3 font-medium">보험 종류</th><th className="px-4 py-3 font-medium">출처·적용일</th><th className="px-4 py-3 font-medium">상태</th><th className="px-4 py-3 font-medium">처리</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => <tr key={item.id}><td className="px-4 py-3"><div className="font-medium text-slate-900">{item.title}</div><div className="mt-1 text-xs text-slate-500">{item.code} · {item.version}판</div></td><td className="px-4 py-3 text-slate-700">{item.insuranceTypeLabel}</td><td className="px-4 py-3 text-slate-700"><div>{item.sourceLabel}</div><div className="mt-1 text-xs text-slate-500">{item.effectiveFrom}부터</div></td><td className="px-4 py-3 font-semibold text-slate-700">{item.statusLabel}</td><td className="px-4 py-3"><div className="flex gap-2">{item.status === 'DRAFT' && <button type="button" onClick={() => changeStatus(item, 'approve')} disabled={saving} className="inline-flex h-8 items-center gap-1 rounded-md border border-emerald-300 px-2 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" aria-hidden="true" />승인</button>}{item.status === 'APPROVED' && <button type="button" onClick={() => changeStatus(item, 'retire')} disabled={saving} className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-300 px-2 text-xs font-semibold text-slate-600"><XCircle className="h-3.5 w-3.5" aria-hidden="true" />사용 종료</button>}</div></td></tr>)}
          </tbody>
        </table>
      </div>
    </section>
  )
}
